import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Shield, Lock, Key, Eye, FileText, Clock, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Download, Trash2,
  Smartphone, History, Bell, UserCheck, Database, Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HIPAACompliance = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // MFA State
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Consent State
  const [consents, setConsents] = useState([]);
  const [requiredConsents, setRequiredConsents] = useState(null);
  
  // Audit State
  const [accessLogs, setAccessLogs] = useState([]);
  
  // Session State
  const [sessions, setSessions] = useState([]);
  
  // Data State
  const [dataInventory, setDataInventory] = useState(null);
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  
  // Dashboard State (admin only)
  const [complianceDashboard, setComplianceDashboard] = useState(null);
  const [securityAlerts, setSecurityAlerts] = useState([]);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin';

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadAllData();
  }, [user, navigate]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load MFA status
      const mfaRes = await axios.get(`${API}/api/hipaa/mfa/status`, { headers });
      setMfaStatus(mfaRes.data);
      
      // Load consents
      const consentsRes = await axios.get(`${API}/api/hipaa/consent`, { headers });
      setConsents(consentsRes.data.consents || []);
      
      // Load required consents
      const reqConsentsRes = await axios.get(`${API}/api/hipaa/consent/required`, { headers });
      setRequiredConsents(reqConsentsRes.data);
      
      // Load access logs
      const logsRes = await axios.get(`${API}/api/hipaa/audit-logs/my-access?days=30`, { headers });
      setAccessLogs(logsRes.data.logs || []);
      
      // Load sessions
      const sessionsRes = await axios.get(`${API}/api/hipaa/sessions`, { headers });
      setSessions(sessionsRes.data.sessions || []);
      
      // Load data inventory
      const inventoryRes = await axios.get(`${API}/api/hipaa/data/inventory`, { headers });
      setDataInventory(inventoryRes.data);
      
      // Admin only - load compliance dashboard
      if (isAdmin) {
        try {
          const dashboardRes = await axios.get(`${API}/api/hipaa/compliance/dashboard`, { headers });
          setComplianceDashboard(dashboardRes.data);
          
          const alertsRes = await axios.get(`${API}/api/hipaa/security/alerts?days=7`, { headers });
          setSecurityAlerts(alertsRes.data.alerts || []);
        } catch (e) {
          process.env.NODE_ENV === 'development' && console.log('Admin endpoints not available');
        }
      }
      
    } catch (error) {
      console.error('Error loading HIPAA data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  // MFA Functions
  const setupMFA = async () => {
    try {
      const token = secureStorage.getToken();
      const res = await axios.post(`${API}/api/hipaa/mfa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfaSetupData(res.data);
      setShowMFASetup(true);
    } catch (error) {
      toast.error('Failed to setup MFA');
    }
  };

  const verifyMFA = async () => {
    try {
      const token = secureStorage.getToken();
      const res = await axios.post(`${API}/api/hipaa/mfa/verify`, { code: mfaCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success('MFA enabled successfully!');
        setBackupCodes(res.data.backup_codes);
        setShowBackupCodes(true);
        setShowMFASetup(false);
        setMfaCode('');
        loadAllData();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error('Invalid verification code');
    }
  };

  const disableMFA = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will reduce your account security.')) {
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      await axios.delete(`${API}/api/hipaa/mfa/disable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MFA disabled');
      loadAllData();
    } catch (error) {
      toast.error('Failed to disable MFA');
    }
  };

  // Session Functions
  const terminateSession = async (sessionId) => {
    try {
      const token = secureStorage.getToken();
      await axios.delete(`${API}/api/hipaa/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Session terminated');
      loadAllData();
    } catch (error) {
      toast.error('Failed to terminate session');
    }
  };

  const terminateAllSessions = async () => {
    if (!window.confirm('This will log you out of all other devices. Continue?')) {
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      const res = await axios.delete(`${API}/api/hipaa/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Terminated ${res.data.terminated_count} sessions`);
      loadAllData();
    } catch (error) {
      toast.error('Failed to terminate sessions');
    }
  };

  // Data Functions
  const exportData = async () => {
    try {
      const token = secureStorage.getToken();
      const res = await axios.get(`${API}/api/hipaa/data/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-health-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const requestDeletion = async () => {
    if (!deletionReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      await axios.post(`${API}/api/hipaa/data/deletion-request`, {
        reason: deletionReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deletion request submitted. You will be notified within 30 days.');
      setShowDeletionDialog(false);
      setDeletionReason('');
    } catch (error) {
      toast.error('Failed to submit deletion request');
    }
  };

  // Consent Functions
  const updateConsent = async (consentType, granted) => {
    try {
      const token = secureStorage.getToken();
      await axios.post(`${API}/api/hipaa/consent`, {
        consent_type: consentType,
        status: granted ? 'granted' : 'denied',
        purpose: `User ${granted ? 'granted' : 'denied'} consent`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Consent ${granted ? 'granted' : 'denied'}`);
      loadAllData();
    } catch (error) {
      toast.error('Failed to update consent');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Privacy & Security</h1>
              <p className="text-gray-600">HIPAA Compliance Center - Manage your data and security</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <TabsTrigger value="overview" className="gap-2">
              <Shield className="w-4 h-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="mfa" className="gap-2">
              <Key className="w-4 h-4" /> MFA
            </TabsTrigger>
            <TabsTrigger value="consents" className="gap-2">
              <UserCheck className="w-4 h-4" /> Consents
            </TabsTrigger>
            <TabsTrigger value="data" className="gap-2">
              <Database className="w-4 h-4" /> My Data
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2">
              <Activity className="w-4 h-4" /> Activity
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">MFA Status</p>
                      <p className="text-2xl font-bold">
                        {mfaStatus?.mfa_enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                    {mfaStatus?.mfa_enabled ? (
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-10 h-10 text-amber-500" />
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Required Consents</p>
                      <p className="text-2xl font-bold">
                        {requiredConsents?.all_granted ? 'Complete' : 'Incomplete'}
                      </p>
                    </div>
                    {requiredConsents?.all_granted ? (
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    ) : (
                      <XCircle className="w-10 h-10 text-red-500" />
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Active Sessions</p>
                      <p className="text-2xl font-bold">{sessions.length}</p>
                    </div>
                    <Smartphone className="w-10 h-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Data Categories</p>
                      <p className="text-2xl font-bold">
                        {dataInventory?.data_categories?.length || 0}
                      </p>
                    </div>
                    <Database className="w-10 h-10 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Security Recommendations */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Security Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {!mfaStatus?.mfa_enabled && (
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <span>Enable Multi-Factor Authentication for enhanced security</span>
                      </div>
                      <Button size="sm" onClick={() => setActiveTab('mfa')}>
                        Enable MFA
                      </Button>
                    </div>
                  )}
                  
                  {!requiredConsents?.all_granted && (
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span>Complete required consents to use all features</span>
                      </div>
                      <Button size="sm" onClick={() => setActiveTab('consents')}>
                        Review Consents
                      </Button>
                    </div>
                  )}
                  
                  {sessions.length > 2 && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-blue-500" />
                        <span>You have {sessions.length} active sessions. Consider reviewing them.</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setActiveTab('activity')}>
                        Review
                      </Button>
                    </div>
                  )}
                  
                  {mfaStatus?.mfa_enabled && requiredConsents?.all_granted && sessions.length <= 2 && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span>Your account security is in good standing!</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MFA Tab */}
          <TabsContent value="mfa">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" /> Multi-Factor Authentication
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">Authenticator App (TOTP)</h3>
                    <p className="text-sm text-gray-500">
                      Use Google Authenticator or similar app
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={mfaStatus?.mfa_enabled ? 'default' : 'secondary'}>
                      {mfaStatus?.mfa_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    {mfaStatus?.mfa_enabled ? (
                      <Button variant="destructive" size="sm" onClick={disableMFA}>
                        Disable
                      </Button>
                    ) : (
                      <Button onClick={setupMFA}>
                        Enable
                      </Button>
                    )}
                  </div>
                </div>
                
                {mfaStatus?.mfa_enabled && (
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-semibold mb-2">Backup Codes</h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {mfaStatus.backup_codes_remaining} backup codes remaining
                    </p>
                    <Button variant="outline" size="sm">
                      Regenerate Backup Codes
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consents Tab */}
          <TabsContent value="consents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" /> Data Consents
                </CardTitle>
                <CardDescription>
                  Manage how your data is used and shared
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Required Consents */}
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">Required Consents</h3>
                  {['treatment', 'payment', 'healthcare_operations'].map((type) => (
                    <div key={type} className="flex items-center justify-between p-3 border rounded-lg mb-2">
                      <div>
                        <p className="font-medium capitalize">{type.replace('_', ' ')}</p>
                        <p className="text-sm text-gray-500">Required for service</p>
                      </div>
                      <Switch
                        checked={requiredConsents?.consents?.[type] || false}
                        onCheckedChange={(checked) => updateConsent(type, checked)}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Optional Consents */}
                <div>
                  <h3 className="font-semibold mb-3">Optional Consents</h3>
                  {['data_sharing', 'research', 'ai_processing', 'analytics'].map((type) => {
                    const consent = consents.find(c => c.consent_type === type);
                    return (
                      <div key={type} className="flex items-center justify-between p-3 border rounded-lg mb-2">
                        <div>
                          <p className="font-medium capitalize">{type.replace('_', ' ')}</p>
                          <p className="text-sm text-gray-500">Optional</p>
                        </div>
                        <Switch
                          checked={consent?.status === 'granted'}
                          onCheckedChange={(checked) => updateConsent(type, checked)}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Data Tab */}
          <TabsContent value="data">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" /> Your Data Inventory
                  </CardTitle>
                  <CardDescription>
                    See what data we have about you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dataInventory?.data_categories?.map((category, idx) => (
                      <div key={`item-${idx}`} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{category.category}</p>
                          <p className="text-sm text-gray-500">
                            {category.record_count} records • Retention: {category.retention_policy}
                          </p>
                        </div>
                        <Badge variant="outline">{category.record_count}</Badge>
                      </div>
                    ))}
                    
                    {(!dataInventory?.data_categories || dataInventory.data_categories.length === 0) && (
                      <p className="text-gray-500 text-center py-4">No data records found</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Data Rights</CardTitle>
                  <CardDescription>
                    Exercise your HIPAA data rights
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">Export Your Data</h3>
                      <p className="text-sm text-gray-500">Download all your health data</p>
                    </div>
                    <Button onClick={exportData}>
                      <Download className="w-4 h-4 mr-2" /> Export
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg border-red-200">
                    <div>
                      <h3 className="font-semibold text-red-700">Request Data Deletion</h3>
                      <p className="text-sm text-gray-500">Request deletion of your data</p>
                    </div>
                    <Button variant="destructive" onClick={() => setShowDeletionDialog(true)}>
                      <Trash2 className="w-4 h-4 mr-2" /> Request Deletion
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <div className="grid gap-6">
              {/* Active Sessions */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Smartphone className="w-5 h-5" /> Active Sessions
                      </CardTitle>
                      <CardDescription>Devices currently logged into your account</CardDescription>
                    </div>
                    {sessions.length > 1 && (
                      <Button variant="outline" size="sm" onClick={terminateAllSessions}>
                        Sign out all other sessions
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sessions.map((session, idx) => (
                      <div key={`item-${idx}`} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">
                            {session.user_agent?.substring(0, 50)}...
                          </p>
                          <p className="text-sm text-gray-500">
                            IP: {session.ip_address} • Last active: {new Date(session.last_activity).toLocaleString()}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => terminateSession(session.session_id)}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Access Logs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" /> Recent PHI Access
                  </CardTitle>
                  <CardDescription>Who accessed your health information</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {accessLogs.slice(0, 20).map((log, idx) => (
                      <div key={`item-${idx}`} className="flex items-center justify-between p-2 border-b text-sm">
                        <div>
                          <span className="font-medium">{log.action}</span>
                          <span className="text-gray-500 ml-2">by {log.user_email || 'System'}</span>
                        </div>
                        <span className="text-gray-400">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    
                    {accessLogs.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No recent access logs</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* MFA Setup Dialog */}
      <Dialog open={showMFASetup} onOpenChange={setShowMFASetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Multi-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {mfaSetupData && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={mfaSetupData.qr_code} alt="QR Code" className="w-48 h-48" />
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-1">Manual entry key:</p>
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                  {mfaSetupData.manual_entry_key}
                </code>
              </div>
              
              <div>
                <Label htmlFor="mfa-code">Verification Code</Label>
                <Input
                  id="mfa-code"
                  placeholder="Enter 6-digit code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  maxLength={6}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMFASetup(false)}>Cancel</Button>
            <Button onClick={verifyMFA} disabled={mfaCode.length !== 6}>Verify & Enable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Your Backup Codes</DialogTitle>
            <DialogDescription>
              Store these codes safely. Each can only be used once.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-2 p-4 bg-gray-100 rounded-lg">
            {backupCodes.map((code, idx) => (
              <code key={`item-${idx}`} className="text-center py-1">{code}</code>
            ))}
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)}>I've saved my codes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deletion Request Dialog */}
      <Dialog open={showDeletionDialog} onOpenChange={setShowDeletionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">Request Data Deletion</DialogTitle>
            <DialogDescription>
              This will request deletion of your health data. The request will be reviewed within 30 days per HIPAA requirements.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="deletion-reason">Reason for deletion</Label>
            <Input
              id="deletion-reason"
              placeholder="Please provide a reason"
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeletionDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={requestDeletion}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HIPAACompliance;
